"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, PhoneCall, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const emergencyFeatures = [{
  id: 1,
  title: "Fast Local Dispatch",
  desc: "Licensed plumbers stationed near Jonesboro ready for rapid arrival."
}, {
  id: 2,
  title: "Upfront Honest Pricing",
  desc: "Clear estimates with no hidden night, weekend, or holiday surprises."
}, {
  id: 3,
  title: "Fully Equipped Trucks",
  desc: "Most burst pipes, leaks, and clogs repaired on the initial visit."
}];
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencyservicecta" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-10 lg:p-14">
          {/* Subtle background glow element */}
          <div className="pointer-events-none absolute -right-20 -top-20 size-80 rounded-full bg-accent/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 size-80 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative z-10 grid gap-10 lg:grid-cols-12 lg:items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="destructive" className="px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                  <AlertTriangle className="size-3.5" />
                  24/7 Rapid Response
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Clock className="size-3.5 text-accent" />
                  Average 30-45 Min Arrival in Jonesboro, GA
                </span>
              </div>

              <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-card-foreground sm:text-4xl lg:text-5xl">
                Plumbing Emergency? Speak With a Plumber Immediately.
              </h2>

              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Burst pipes, major sewage backups, and broken water heaters cannot wait. 
                Our Jonesboro emergency technicians are available around the clock to stop damage and restore your plumbing safely.
              </p>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Button asChild size="lg" className="h-14 bg-primary px-8 text-base font-semibold text-primary-foreground shadow-md transition-transform hover:-translate-y-0.5 hover:bg-primary/90">
                  <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                    <PhoneCall className="size-5 animate-pulse text-accent" />
                    Call (470) 847-9076
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="h-14 border-border bg-background px-6 text-base font-semibold text-foreground hover:bg-secondary hover:text-secondary-foreground">
                  <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                    Request Online Service
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>

              <p className="mt-3 text-xs text-muted-foreground">
                Zero dispatch delays. Direct connection to emergency on-call technicians.
              </p>
            </div>

            {/* Right Feature Card */}
            <div className="lg:col-span-5">
              <div className="rounded-xl border border-border bg-background p-6 shadow-sm sm:p-8">
                <div className="flex items-center gap-3 border-b border-border pb-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-default text-base font-semibold text-foreground">
                      Jonesboro Guaranteed Service
                    </h3>
                    <p className="text-xs text-muted-foreground">Licensed, Bonded &amp; Insured Plumbers</p>
                  </div>
                </div>

                <ul className="mt-5 space-y-4">
                  {emergencyFeatures.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-chart-4" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">{item.title}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>

                <div className="mt-6 rounded-lg bg-secondary/80 p-4">
                  <div className="flex items-center gap-2">
                    <Wrench className="size-4 text-secondary-foreground" />
                    <span className="text-xs font-semibold text-secondary-foreground">
                      Common Emergencies Handled:
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="border-border bg-background text-[11px] text-foreground">
                      Burst Pipes
                    </Badge>
                    <Badge variant="outline" className="border-border bg-background text-[11px] text-foreground">
                      Sewer Backups
                    </Badge>
                    <Badge variant="outline" className="border-border bg-background text-[11px] text-foreground">
                      Water Heater Leaks
                    </Badge>
                    <Badge variant="outline" className="border-border bg-background text-[11px] text-foreground">
                      Overflowing Toilets
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}